import { Fragment, useEffect, useRef, useState } from 'react'
import Header from '../../organisms/Header/Header'
import homepageBg from '../../../assets/images/homepage/homepagebg.webp'
import smallbox from '../../../assets/images/smallbox.webp'
import bigbox from '../../../assets/images/bigbox.webp'
import colors from '../../theme/color'
import CommonHeroSection from '../../organisms/commonherosection/CommonHeroSection'

export type HeroTitleSegment = {
  text: string
  emphasis?: 'purple'
}

export type HeroTitleLine = HeroTitleSegment[]

export type CommanHeroSectionProps = {
  backgroundSrc?: string
  backgroundAlt?: string
  titleLines: HeroTitleLine[]
}

const CommanHeroSection = ({
  backgroundSrc = homepageBg,
  backgroundAlt = 'Hero background',
  titleLines,
}: CommanHeroSectionProps) => {

  // 🔥 Glow refs
  const glow1Ref = useRef<HTMLDivElement>(null)
  const glow2Ref = useRef<HTMLDivElement>(null)

  // 🔥 Heading animation ref
  const headingRef = useRef<HTMLHeadingElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  // 🔥 Glow animation
  useEffect(() => {
    const section = glow1Ref.current?.parentElement
    if (!section) return

    let x1 = 100, y1 = 100
    let x2 = 300, y2 = 200

    let dx1 = 0.3
    let dy1 = 0.25

    let dx2 = -0.25
    let dy2 = 0.35

    const animate = () => {
      const rect = section.getBoundingClientRect()

      x1 += dx1
      y1 += dy1

      x2 += dx2
      y2 += dy2

      if (x1 > rect.width - 200 || x1 < 0) dx1 *= -1
      if (y1 > rect.height - 200 || y1 < 0) dy1 *= -1

      if (x2 > rect.width - 250 || x2 < 0) dx2 *= -1
      if (y2 > rect.height - 250 || y2 < 0) dy2 *= -1

      if (glow1Ref.current) {
        glow1Ref.current.style.transform = `translate(${x1}px, ${y1}px)`
      }

      if (glow2Ref.current) {
        glow2Ref.current.style.transform = `translate(${x2}px, ${y2}px)`
      }

      requestAnimationFrame(animate)
    }

    animate()
  }, [])

  // 🔥 Intersection Observer for H1 animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting)
      },
      {
        threshold: 0.4,
      }
    )

    if (headingRef.current) {
      observer.observe(headingRef.current)
    }

    return () => {
      if (headingRef.current) observer.unobserve(headingRef.current)
    }
  }, [])

  return (
    <CommonHeroSection>

      <style>{`
        @keyframes rotateSlow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes rotateReverse {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }

        .rotate-slow {
          animation: rotateSlow 12s linear infinite;
        }

        .rotate-reverse {
          animation: rotateReverse 14s linear infinite;
        }

        /* 🔥 H1 animation */
        @keyframes scaleUpFade {
          0% {
            transform: translateY(40px) scale(0.8);
            opacity: 0;
          }
          100% {
            transform: translateY(0px) scale(1);
            opacity: 1;
          }
        }

        .hero-hidden {
          opacity: 0;
          transform: translateY(40px) scale(0.8);
        }

        .hero-visible {
          animation: scaleUpFade 0.8s ease-in forwards;
        }
      `}</style>

      <section>
        {/* Header */}
        <div className="relative">
          <Header />
        </div>

        {/* Content */}
        <div className="relative flex min-h-[350px] md:min-h-[50vh] lg:min-h-[60vh] 2xl:min-h-[50vh] items-center justify-center px-6 md:px-10 lg:px-16 xl:px-18 2xl:px-20">
          <div className="mx-auto flex w-full max-w-8xl items-center justify-center gap-6 md:gap-10 lg:gap-10 xl:gap-35 2xl:gap-30">

            <img
              src={smallbox}
              alt=""
              aria-hidden
              className="self-center h-5 w-5 md:h-6 md:w-6 lg:h-5 lg:w-5 xl:h-7 xl:w-7 2xl:h-10 2xl:w-10 object-contain opacity-90 rotate-slow"
            />

                <h1
                  ref={headingRef}
                  className={`text-center font-rajdhani
                    text-[34px]
                    leading-[1]
                    sm:leading-tight
                    font-bold text-white
                    sm:text-4xl
                    md:text-5xl
                    lg:text-5xl
                    xl:text-6xl
                    2xl:text-7xl
                    ${isVisible ? 'hero-visible' : 'hero-hidden'}
                    `}
                >
                              {titleLines.map((segments, lineIndex) => (
                <Fragment key={lineIndex}>
                  <span>
                    {segments.map((seg, segIndex) => (
                      <span
                        key={segIndex}
                        className={seg.emphasis === 'purple' ? 'text-purple' : undefined}
                      >
                        {seg.text}
                      </span>
                    ))}
                  </span>
                  {lineIndex < titleLines.length - 1 ? (
  lineIndex === 0 ? (
    <>
      <br className="sm:hidden" />
      <br className="hidden sm:block" />
    </>
  ) : (
    <br />
  )
) : null}
                </Fragment>
              ))}
            </h1>

            <img
              src={bigbox}
              alt=""
              aria-hidden
              className="self-start mt-1 h-8 w-8 md:h-10 md:w-10 lg:h-15 lg:w-15 xl:h-20 xl:w-20 2xl:h-25 2xl:w-25 object-contain opacity-90 rotate-reverse"
            />
          </div>
        </div>
      </section>
    </CommonHeroSection>
  )
}

export default CommanHeroSection